// components/ProductPage.tsx

"use client";

import React from "react";
import styled from "styled-components";

// Import sub-components

import { OverviewSection } from "./_sections";
import { FeaturesSection } from "./_sections";
import { TechnicalSpecsSection } from "./_sections";
import { HowItWorksSection } from "./_sections";
import { PricingSection } from "./_sections";
import { SecuritySection } from "./_sections";
import { UseCasesSection } from "./_sections";
import { GettingStartedSection } from "./_sections";
import { RoadmapSection } from "./_sections";
import { EcosystemSection } from "./_sections";
import { CTASection } from "./_sections";
import { HeroSection } from "./_sections";
import { ProductPageData } from "../../_sys/_types";

import GithubDownloadTracker from "@/components/functional/GithubDownloadTracker";
import MajikMessageHowItWorksChart from "@/components/functional/ReactFlow/MajikMessageHowItWorksChart";
import HeroVideoSection from "@/components/sections/HeroVideoSection";

// ============================================
// STYLED COMPONENTS
// ============================================

const RootContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  background: ${({ theme }) => theme.colors.primaryBackground};
`;

const PageWrapper = styled.main`
  width: 100%;
  display: flex;
  flex-direction: column;

  margin: 25px auto;
  padding: 0;
  align-items: center;

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.6;
`;

const Section = styled.section<{ $background?: boolean; $noPadding?: boolean }>`
  padding: ${(props) => (props.$noPadding ? "0" : "80px 36px")};
  background: ${({ theme, $background }) =>
    $background ? theme.gradients.glass : "transparent"};
  border-radius: 16px;
  max-width: ${(props) => (props.$noPadding ? "unset" : "1200px")};

  @media (max-width: 768px) {
    padding: ${(props) => (props.$noPadding ? "0" : "48px 20px")};
  }
`;
// ============================================
// MAIN COMPONENT
// ============================================

interface ProductPageProps {
  data: ProductPageData;
}

export const ProductViewer: React.FC<ProductPageProps> = ({ data }) => {
  return (
    <RootContainer>
      <Section $noPadding>
        <HeroSection data={data.hero} metadata={data.metadata} />
      </Section>
      <PageWrapper>
        {data.metadata.video.length > 0 && (
          <HeroVideoSection videoID={data.metadata.video[0]} />
        )}

        {/* Overview */}
        <Section id="overview">
          <OverviewSection data={data.overview} />
        </Section>

        {/* Features */}
        <Section id="features">
          <FeaturesSection features={data.features} />
        </Section>

        {/* Technical Specs */}
        <Section id="technical-specs">
          <TechnicalSpecsSection data={data.technicalSpecs} />
        </Section>

        {/* How It Works */}
        <Section id="how-it-works" $background={true}>
          <HowItWorksSection data={data.howItWorks} />
        </Section>
        {data.id === "majik-message" && (
          <MajikMessageHowItWorksChart expand={true} />
        )}

        {/* Pricing */}
        <Section id="pricing">
          <PricingSection data={data.pricing} />
        </Section>

        {/* Security Considerations */}
        <Section id="security">
          <SecuritySection data={data.security} />
        </Section>

        {/* Use Cases */}
        <Section id="use-cases">
          <UseCasesSection useCases={data.useCases} />
        </Section>

        {/* Getting Started */}
        <Section id="getting-started" $background={true}>
          <GettingStartedSection data={data.gettingStarted} />
        </Section>

        {/* Roadmap */}
        <Section id="roadmap">
          <RoadmapSection data={data.roadmap} />
        </Section>

        {/* Ecosystem */}
        <Section id="ecosystem" $background={true}>
          <EcosystemSection data={data.ecosystem} />
        </Section>
        {!!data.repository?.repo && !!data.repository?.owner && (
          <GithubDownloadTracker
            repo={{
              owner: data.repository.owner,
              repo: data.repository.repo,
            }}
          />
        )}
      </PageWrapper>
      {/* CTA / Download */}
      <Section id="download" $noPadding>
        <CTASection data={data.cta} />
      </Section>
    </RootContainer>
  );
};

export default ProductViewer;
